import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Globe, Shield, Zap, Smartphone, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ATMCardsPage() {
  const navigate = useNavigate();

  const cardTypes = [
    {
      name: 'Virtual Card',
      price: 'Free',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070&auto=format&fit=crop',
      features: ['Instant issuance', 'Online payments', 'Freeze/unfreeze anytime', 'Multiple cards'],
      limits: { daily: '₦500,000', monthly: '₦5M' }
    },
    {
      name: 'Physical Card',
      price: '₦2,000',
      image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?q=80&w=2070&auto=format&fit=crop',
      features: ['Global acceptance', 'ATM withdrawals', 'POS payments', 'Contactless'],
      limits: { daily: '₦1M', monthly: '₦10M' },
      popular: true
    },
    {
      name: 'Premium Card',
      price: '₦5,000',
      image: 'https://images.unsplash.com/photo-1565373679610-42f9d3de3f4b?q=80&w=2070&auto=format&fit=crop',
      features: ['Priority support', 'Airport lounge access', 'Travel insurance', 'Cashback rewards'],
      limits: { daily: '₦5M', monthly: '₦50M' }
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
                Britedge ATM Cards
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Spend globally with virtual and physical cards. Secure, flexible, and accepted everywhere Visa/Mastercard is accepted.
              </p>
              <div className="flex gap-4">
                <Button className="bg-brand-blue text-white rounded-full px-8 py-6 text-lg font-semibold">
                  Order Card Now
                </Button>
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg">
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1556742111-a301076d9d18?q=80&w=2070&auto=format&fit=crop"
                alt="Britedge Card" 
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-heading text-slate-900 mb-12 text-center">Choose Your Card</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {cardTypes.map((card, idx) => (
              <div key={idx} className={`relative bg-white rounded-3xl p-6 shadow-lg border-2 ${card.popular ? 'border-brand-blue scale-105' : 'border-slate-200'}`}>
                {card.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-blue text-white px-4 py-1 rounded-full text-sm font-bold">Most Popular</span>
                  </div>
                )}
                <img src={card.image} alt={card.name} className="w-full h-48 object-cover rounded-2xl mb-6" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{card.name}</h3>
                <div className="text-4xl font-bold text-brand-blue mb-6">{card.price}</div>
                
                <div className="space-y-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-600 mb-1">Daily Limit</p>
                    <p className="font-bold text-slate-900">{card.limits.daily}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-600 mb-1">Monthly Limit</p>
                    <p className="font-bold text-slate-900">{card.limits.monthly}</p>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {card.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2 text-slate-700 text-sm">
                      <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full bg-brand-blue text-white rounded-xl py-4">
                  Order {card.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-heading text-slate-900 mb-12 text-center">Card Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Globe className="w-8 h-8" />, title: 'Global Acceptance', desc: 'Use anywhere Visa/Mastercard is accepted' },
              { icon: <Zap className="w-8 h-8" />, title: 'Instant Virtual Cards', desc: 'Create and use immediately' },
              { icon: <Shield className="w-8 h-8" />, title: 'Secure Payments', desc: '3D Secure & fraud protection' },
              { icon: <Smartphone className="w-8 h-8" />, title: 'Mobile Control', desc: 'Manage cards from your phone' }
            ].map((feature, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 text-brand-blue flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 text-white">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Lock className="w-16 h-16 mb-6" />
                <h2 className="text-4xl font-bold font-heading mb-6">Bank-Level Security</h2>
                <ul className="space-y-4">
                  {['EMV chip & PIN protection', '3D Secure authentication', 'Real-time fraud monitoring', 'Instant freeze/unfreeze', 'Zero liability on unauthorized transactions'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">✓</span>
                      <span className="text-blue-50">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-4">Card Protection</h3>
                <p className="text-blue-100 mb-6">
                  All Britedge cards come with comprehensive protection against fraud and unauthorized use.
                </p>
                <Button className="bg-white text-brand-blue hover:bg-indigo-50 rounded-xl px-6 py-3">
                  Learn About Security
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold font-heading mb-6">Get Your Britedge Card Today</h2>
          <p className="text-xl text-green-100 mb-8">
            Join thousands already enjoying seamless global payments
          </p>
          <Button 
            onClick={() => navigate('/dashboard/cards')}
            className="bg-white text-green-600 hover:bg-green-50 rounded-full px-10 py-6 text-lg font-semibold"
          >
            Order Your Card Now
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
